/**
 * Asset URL helpers — CDN-first, Vite BASE_URL fallback.
 *
 * CDN URL  : VITE_CDN_URL + path   — no basename, shared across all modules
 *            e.g. https://cdn.example.com/asset/flags/gb.png
 *
 * Fallback : import.meta.env.BASE_URL + path — Vite injects the basename
 *            e.g. /user/asset/flags/gb.png
 *
 * VITE_CDN_URL is an optional infra-level variable (CI/CD build arg).
 * It is never hardcoded in module .env files.
 */

const cdnOrigin = (import.meta.env.VITE_CDN_URL ?? "").replace(/\/$/, "");

function clean(path: string): string {
  return path.startsWith("/") ? path.slice(1) : path;
}

/** CDN URL for an asset — no basename, null when CDN is not configured. */
export function getCdnUrl(path: string): string | null {
  if (!cdnOrigin) return null;
  return `${cdnOrigin}/${clean(path)}`;
}

/** Fallback URL using Vite BASE_URL (includes the module basename). */
export function getFallbackUrl(path: string): string {
  return `${import.meta.env.BASE_URL}${clean(path)}`;
}

/**
 * Best available URL: CDN when VITE_CDN_URL is set, basename path otherwise.
 * Use for <img src>, <link href>, fetch(), etc.
 */
export function getAssetUrl(path: string): string {
  return getCdnUrl(path) ?? getFallbackUrl(path);
}

/** CSS url("…") wrapper — for use in backgroundImage / inline styles. */
export function getAssetCssUrl(path: string): string {
  return `url("${getAssetUrl(path)}")`;
}
